/**
 * Neighbor data fetching and normalization composable.
 *
 * Handles fetching production data for neighbor meters, their detail info
 * (for Wp normalization), and provides normalization controls.
 */

import { ref, computed, watch } from 'vue'
import { useQueries } from '@tanstack/vue-query'
import { format, min } from 'date-fns'
import { startOfToday } from 'date-fns'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { MAX_NEIGHBORS } from '../constants'

export function useNeighborData(props, level, dateRange) {
  const today = startOfToday()
  const showNeighbors = ref(false)
  const showNormalized = ref(false)

  // Sync normalization with neighbors: on by default when neighbors active,
  // off when neighbors off (but can be manually toggled while active)
  watch(showNeighbors, (on) => {
    showNormalized.value = on
  })
  watch(() => props.neighborMatchType, (type) => {
    if (type === 'postcode') showNormalized.value = true
  })

  const mainTotalWp = computed(() =>
    props.numOfPanels && props.panelWp ? props.numOfPanels * props.panelWp : null
  )

  const activeNeighbors = computed(() =>
    showNeighbors.value ? props.neighborMeters.slice(0, MAX_NEIGHBORS) : []
  )

  // Fetch neighbor production data
  const fetchNeighborData = async (serial) => {
    const { from, until } = dateRange.value
    try {
      let result
      if (level.value === 'day') {
        const urlParams = new URLSearchParams({
          date_from: from.toISOString(),
          date_until: until.toISOString(),
        })
        result = await fetchData(`${READINGS_API_BASE}/meters/${serial}/quarter-hour-deltas?${urlParams}`)
      } else if (level.value === 'month') {
        const urlParams = new URLSearchParams({
          serial_number: serial,
          date_from: format(from, 'yyyy-MM-dd'),
          date_till: format(min([until, today]), 'yyyy-MM-dd'),
          limit: '250',
        })
        result = await fetchData(`${READINGS_API_BASE}/daily-aggregates?${urlParams}`)
      } else {
        const urlParams = new URLSearchParams({
          serial_number: serial,
          date_from: format(from, 'yyyy-MM-dd'),
          date_till: format(min([until, today]), 'yyyy-MM-dd'),
        })
        result = await fetchData(`${READINGS_API_BASE}/month-aggregates?${urlParams}`)
      }
      return result?.status === 'OK' ? result.data : []
    } catch {
      return []
    }
  }

  const neighborQueries = computed(() =>
    activeNeighbors.value.map(meter => ({
      queryKey: ['neighborGraph', meter.serialNumber, level.value,
        dateRange.value.from?.toISOString(), dateRange.value.until?.toISOString()],
      queryFn: () => fetchNeighborData(meter.serialNumber),
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }))
  )

  const neighborResults = useQueries({ queries: neighborQueries })

  // Vue 2 compatible: use watch instead of computed for useQueries results
  const neighborDataMap = ref({})
  watch(neighborResults, (results) => {
    const map = {}
    if (!results) return
    for (let idx = 0; idx < results.length; idx++) {
      const query = results[idx]
      const meter = activeNeighbors.value[idx]
      if (meter && query?.data) map[meter.serialNumber] = query.data
    }
    neighborDataMap.value = map
  }, { deep: true, immediate: true })

  // Fetch neighbor meter details (for Wp normalization)
  const neighborDetailQueries = computed(() =>
    activeNeighbors.value.map(meter => ({
      queryKey: ['meterDetail', meter.serialNumber],
      queryFn: async () => {
        const result = await fetchData(`/eleena/api/v1/meters/${meter.serialNumber}`)
        return result?.status === 'OK' ? result.data : null
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }))
  )

  const neighborDetailResults = useQueries({ queries: neighborDetailQueries })

  const neighborWpMap = ref({})
  const neighborDetailMap = ref({})
  watch(neighborDetailResults, (results) => {
    const wpMap = {}
    const detailMap = {}
    if (!results) return
    for (let idx = 0; idx < results.length; idx++) {
      const query = results[idx]
      const meter = activeNeighbors.value[idx]
      if (meter && query?.data) {
        detailMap[meter.serialNumber] = query.data
        const wp = (query.data.num_of_panels ?? 0) * (query.data.panel_wp ?? 0)
        if (wp > 0) wpMap[meter.serialNumber] = wp
      }
    }
    neighborWpMap.value = wpMap
    neighborDetailMap.value = detailMap
  }, { deep: true, immediate: true })

  // Show normalize button whenever neighbors are active
  const showNormalizeButton = computed(() =>
    showNeighbors.value && activeNeighbors.value.length > 0 && !!mainTotalWp.value
  )

  // Normalize values to Wh/kWp
  const normalizeValues = (data, totalWp) => {
    if (!showNormalized.value || !totalWp || totalWp <= 0) return data
    const kWp = totalWp * 0.001
    return data.map(v => v != null ? Math.round((v / kWp) * 10) / 10 : v)
  }

  return {
    showNeighbors,
    showNormalized,
    mainTotalWp,
    activeNeighbors,
    neighborDataMap,
    neighborWpMap,
    neighborDetailMap,
    showNormalizeButton,
    normalizeValues,
  }
}
